import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, User as UserIcon, Building2, Car, Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { validateNpa, npaPlaceholder } from "@/lib/npa";
import { useNpaLookup } from "@/lib/useNpaLookup";

const EMPTY_CLIENT = {
  type: "particulier",
  civilite: "M.",
  prenom: "",
  nom: "",
  raison_sociale: "",
  ide: "",
  contact_nom: "",
  adresse: "",
  npa: "",
  localite: "",
  pays: "CH",
  tel_principal: "",
  tel_secondaire: "",
  email: "",
  email_factures: "",  // iter206 — email dédié envoi factures (assurance/entreprise)
  langue: "FR",
  num_bexio: "",
  statut: "actif",
  abonnement: "aucun",
  is_parc: false,  // iter120 — client Parc (active module OR Parc + facturation groupée)
  notes: "",
};

const EMPTY_VEHICULE = {
  plaque: "",
  vin: "",
  marque: "",
  modele: "",
  premiere_mec: "",
  carburant: "essence",
  km_dernier: "",
  type_entretien: "standard",
};

export default function ClientForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [client, setClient] = useState(EMPTY_CLIENT);
  const [addVehicule, setAddVehicule] = useState(false);
  const [vehicule, setVehicule] = useState(EMPTY_VEHICULE);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  // iter163.3 — Validation persistante + auto-scroll pour ne plus manquer les erreurs
  const [errors, setErrors] = useState({});
  const fieldRefs = useRef({});
  const registerRef = (name) => (el) => { if (el) fieldRefs.current[name] = el; };

  // iter164.5 — Auto-complétion NPA → Localité
  const { options: localiteOptions, loading: localiteLoading } = useNpaLookup(
    client.npa, client.pays, client.localite,
    (name) => setClient((c) => ({ ...c, localite: name })),
  );

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}`);
        setClient({ ...EMPTY_CLIENT, ...data });
      } catch (e) {
        toast.error("Client introuvable");
        navigate("/clients");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleChange = (field, value) => {
    setClient((c) => ({ ...c, [field]: value }));
    // Efface l'erreur du champ modifié
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };
  const handleVehChange = (field, value) => setVehicule((v) => ({ ...v, [field]: value }));

  const validate = () => {
    const errs = {};
    // iter164.4 — Validation NPA via util partagé /lib/npa.js
    const npaCheck = validateNpa(client.npa, client.pays);
    if (!npaCheck.ok) errs.npa = npaCheck.message;
    if (!client.adresse) errs.adresse = "Adresse requise";
    if (!client.localite) errs.localite = "Localité requise";
    if (!client.tel_principal) errs.tel_principal = "Téléphone principal requis";
    if (client.type === "particulier") {
      if (!client.prenom) errs.prenom = "Prénom requis pour un particulier";
      if (!client.nom) errs.nom = "Nom requis pour un particulier";
    } else if ((client.type === "entreprise" || client.type === "assurance") && !client.raison_sociale) {
      errs.raison_sociale = client.type === "assurance"
        ? "Raison sociale requise pour une assurance"
        : "Raison sociale requise pour une entreprise";
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      // Scroll vers le premier champ en erreur + focus
      const firstKey = Object.keys(errs)[0];
      const el = fieldRefs.current[firstKey];
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
        setTimeout(() => { try { el.focus(); } catch { /* noop */ } }, 300);
      }
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Clean empty strings to null for optional fields
      const payload = Object.fromEntries(
        Object.entries(client).map(([k, v]) => [k, v === "" ? null : v])
      );
      // Required fields stay strings
      payload.adresse = client.adresse;
      payload.npa = client.npa;
      payload.localite = client.localite;
      payload.tel_principal = client.tel_principal;
      payload.type = client.type;
      payload.langue = client.langue;
      payload.statut = client.statut;
      payload.pays = client.pays;
      payload.abonnement = client.abonnement;
      payload.is_parc = !!client.is_parc;

      if (isEdit) {
        await api.put(`/clients/${id}`, payload);
        toast.success("Client mis à jour");
        navigate(`/clients/${id}`);
      } else {
        // Embed first vehicule if filled
        if (addVehicule && vehicule.plaque && vehicule.marque && vehicule.modele) {
          const v = Object.fromEntries(
            Object.entries(vehicule).map(([k, val]) => [k, val === "" ? null : val])
          );
          if (v.km_dernier) v.km_dernier = parseInt(v.km_dernier, 10);
          // iter69 — Dérive annee depuis premiere_mec (rétrocompat affichage)
          if (v.premiere_mec && v.premiere_mec.length >= 4) {
            v.annee = parseInt(v.premiere_mec.slice(0, 4), 10);
          }
          payload.first_vehicule = v;
        }
        const { data } = await api.post("/clients", payload);
        toast.success("Client créé");
        navigate(`/clients/${data.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="client-form-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
        {isEdit ? "Modifier le client" : "Nouveau client"}
      </h1>

      {Object.keys(errors).length > 0 && (
        <div
          className="mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3"
          role="alert"
          data-testid="client-form-error-banner"
        >
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-red-800 mb-1">
              Impossible d&apos;enregistrer — {Object.keys(errors).length} champ{Object.keys(errors).length > 1 ? "s" : ""} à corriger :
            </div>
            <ul className="list-disc list-inside text-red-700 space-y-0.5">
              {Object.entries(errors).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Tabs
        value={client.type}
        onValueChange={(v) => {
          if (v === client.type) return;
          // iter220 — En mode édition, on autorise le changement de type
          // (corriger une erreur de saisie) mais on demande confirmation
          // car cela peut effacer/altérer les champs prenom/nom/raison_sociale
          // et changer le format d'affichage sur les OR/factures/devis liés.
          if (isEdit) {
            const from = client.type === "particulier" ? "Particulier" : client.type === "entreprise" ? "Entreprise" : "Assurance";
            const to = v === "particulier" ? "Particulier" : v === "entreprise" ? "Entreprise" : "Assurance";
            const ok = window.confirm(
              `Changer le type de client (${from} → ${to}) ?\n\n` +
              `Les OR/factures/devis existants seront mis à jour pour afficher le nouveau libellé (raison sociale ou prénom+nom).\n\n` +
              `Assure-toi de remplir les champs correspondants au nouveau type avant d'enregistrer.`,
            );
            if (!ok) return;
          }
          handleChange("type", v);
        }}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="particulier" data-testid="tab-particulier">
            <UserIcon className="w-4 h-4 mr-2" /> Particulier
          </TabsTrigger>
          <TabsTrigger value="entreprise" data-testid="tab-entreprise">
            <Building2 className="w-4 h-4 mr-2" /> Entreprise
          </TabsTrigger>
          <TabsTrigger value="assurance" data-testid="tab-assurance">
            <ShieldCheck className="w-4 h-4 mr-2" /> Assurance
          </TabsTrigger>
        </TabsList>

        <Card className="p-6 border-gray-200">
          {/* Identité */}
          <h2 className="font-heading font-semibold text-gray-900 mb-3">Identité</h2>
          {client.type === "particulier" ? (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Civilité</Label>
                <Select value={client.civilite || "M."} onValueChange={(v) => handleChange("civilite", v)}>
                  <SelectTrigger data-testid="field-civilite"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">M.</SelectItem>
                    <SelectItem value="Mme">Mme</SelectItem>
                    <SelectItem value="Mlle">Mlle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prénom *</Label>
                <Input
                  ref={registerRef("prenom")}
                  value={client.prenom || ""}
                  onChange={(e) => handleChange("prenom", e.target.value)}
                  className={errors.prenom ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="field-prenom"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  ref={registerRef("nom")}
                  value={client.nom || ""}
                  onChange={(e) => handleChange("nom", e.target.value)}
                  className={errors.nom ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="field-nom"
                />
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <Label>{client.type === "assurance" ? "Raison sociale (compagnie d'assurance) *" : "Raison sociale *"}</Label>
                <Input
                  ref={registerRef("raison_sociale")}
                  value={client.raison_sociale || ""}
                  onChange={(e) => handleChange("raison_sociale", e.target.value)}
                  className={errors.raison_sociale ? "border-red-500 focus-visible:ring-red-500" : ""}
                  data-testid="field-raison-sociale"
                />
              </div>
              <div>
                <Label>IDE / N° TVA</Label>
                <Input value={client.ide || ""} onChange={(e) => handleChange("ide", e.target.value)} placeholder="CHE-123.456.789" data-testid="field-ide" />
              </div>
              <div>
                <Label>{client.type === "assurance" ? "Contact / service sinistres" : "Contact (interne)"}</Label>
                <Input value={client.contact_nom || ""} onChange={(e) => handleChange("contact_nom", e.target.value)} data-testid="field-contact-nom" />
              </div>
            </div>
          )}

          {/* Adresse */}
          <h2 className="font-heading font-semibold text-gray-900 mb-3">Adresse</h2>
          <div className="grid md:grid-cols-6 gap-4 mb-6">
            <div className="md:col-span-6">
              <Label>Adresse *</Label>
              <Input
                ref={registerRef("adresse")}
                value={client.adresse}
                onChange={(e) => handleChange("adresse", e.target.value)}
                placeholder="Rue, n°"
                className={errors.adresse ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="field-adresse"
              />
            </div>
            <div className="md:col-span-1">
              <Label>Code postal *</Label>
              <Input
                ref={registerRef("npa")}
                value={client.npa}
                onChange={(e) => {
                  // iter163.5 — Accepte jusqu'à 10 chars alphanumériques quelle que soit
                  // la valeur de pays (CH 4 chiffres, FR 5 chiffres, autres…). Le format
                  // exact est vérifié à la sauvegarde selon pays.
                  const v = e.target.value.replace(/[^A-Za-z0-9 \-]/g, "").slice(0, 10);
                  handleChange("npa", v);
                }}
                maxLength={10}
                placeholder={npaPlaceholder(client.pays)}
                className={errors.npa ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="field-npa"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="flex items-center gap-1.5">
                Localité *
                {localiteLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                {!localiteLoading && localiteOptions.length > 0 && (
                  <span className="text-[10px] text-emerald-600 font-normal">
                    {localiteOptions.length === 1 ? "auto-complété" : `${localiteOptions.length} choix`}
                  </span>
                )}
              </Label>
              <Input
                ref={registerRef("localite")}
                value={client.localite}
                onChange={(e) => handleChange("localite", e.target.value)}
                placeholder="Meyrin"
                list="client-loc-options"
                className={errors.localite ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="field-localite"
              />
              <datalist id="client-loc-options">
                {localiteOptions.map((opt, i) => (
                  <option key={i} value={opt.name}>{opt.canton ? `${opt.name} (${opt.canton})` : opt.name}</option>
                ))}
              </datalist>
            </div>
            <div className="md:col-span-2">
              <Label>Pays</Label>
              <select
                value={client.pays || "CH"}
                onChange={(e) => handleChange("pays", e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="field-pays"
              >
                <option value="CH">🇨🇭 Suisse</option>
                <option value="FR">🇫🇷 France</option>
                <option value="DE">🇩🇪 Allemagne</option>
                <option value="IT">🇮🇹 Italie</option>
                <option value="AT">🇦🇹 Autriche</option>
                <option value="LI">🇱🇮 Liechtenstein</option>
                <option value="">Autre</option>
              </select>
            </div>
          </div>

          {/* Contact */}
          <h2 className="font-heading font-semibold text-gray-900 mb-3">Contact</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Téléphone principal *</Label>
              <Input
                ref={registerRef("tel_principal")}
                value={client.tel_principal}
                onChange={(e) => handleChange("tel_principal", e.target.value)}
                placeholder="+41 79 ..."
                className={errors.tel_principal ? "border-red-500 focus-visible:ring-red-500" : ""}
                data-testid="field-tel-principal"
              />
            </div>
            <div>
              <Label>Téléphone secondaire</Label>
              <Input value={client.tel_secondaire || ""} onChange={(e) => handleChange("tel_secondaire", e.target.value)} data-testid="field-tel-secondaire" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={client.email || ""} onChange={(e) => handleChange("email", e.target.value)} data-testid="field-email" />
            </div>
            {(client.type === "entreprise" || client.type === "assurance") && (
              <div>
                <Label>Email envoi factures {client.email_factures ? "" : <span className="text-xs text-gray-500">(par défaut : Email principal ci-dessus)</span>}</Label>
                <Input
                  type="email"
                  value={client.email_factures || ""}
                  onChange={(e) => handleChange("email_factures", e.target.value)}
                  placeholder={client.email || "compta@compagnie.ch"}
                  data-testid="field-email-factures"
                />
              </div>
            )}
            <div>
              <Label>Langue de correspondance</Label>
              <Select value={client.langue} onValueChange={(v) => handleChange("langue", v)}>
                <SelectTrigger data-testid="field-langue"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FR">Français</SelectItem>
                  <SelectItem value="DE">Allemand</SelectItem>
                  <SelectItem value="IT">Italien</SelectItem>
                  <SelectItem value="EN">Anglais</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Métier */}
          <h2 className="font-heading font-semibold text-gray-900 mb-3">Statut & abonnement</h2>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div>
              <Label>Statut</Label>
              <Select value={client.statut} onValueChange={(v) => handleChange("statut", v)}>
                <SelectTrigger data-testid="field-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="inactif">Inactif</SelectItem>
                  <SelectItem value="bloque">Bloqué</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Abonnement CarMaintenance</Label>
              <Select value={client.abonnement || "aucun"} onValueChange={(v) => handleChange("abonnement", v)}>
                <SelectTrigger data-testid="field-abonnement"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucun">Sans abonnement</SelectItem>
                  <SelectItem value="S">Abonnement S</SelectItem>
                  <SelectItem value="M">Abonnement M</SelectItem>
                  <SelectItem value="L">Abonnement L</SelectItem>
                  <SelectItem value="flotte">Flotte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>N° client Bexio</Label>
              <Input value={client.num_bexio || ""} onChange={(e) => handleChange("num_bexio", e.target.value)} data-testid="field-num-bexio" />
            </div>
          </div>

          <div>
            <Label>Notes libres</Label>
            <Textarea value={client.notes || ""} onChange={(e) => handleChange("notes", e.target.value)} rows={3} data-testid="field-notes" />
          </div>

          {/* iter120 — Toggle Client Parc */}
          <div className="mt-4 p-3 border border-blue-200 bg-blue-50 rounded-lg flex items-center justify-between">
            <div>
              <Label className="font-medium text-blue-900">Client Parc (flotte)</Label>
              <div className="text-[11px] text-blue-700 mt-0.5">
                Active le module OR Parc ultra-rapide (articles pré-définis, sans RDV) et la facturation groupée à la demande.
              </div>
            </div>
            <Switch
              checked={!!client.is_parc}
              onCheckedChange={(v) => handleChange("is_parc", v)}
              data-testid="field-is-parc"
            />
          </div>
        </Card>

        {/* Premier véhicule */}
        {!isEdit && (
          <Card className="p-6 mt-4 border-gray-200" data-testid="first-vehicule-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-[#52a675]" />
                <h2 className="font-heading font-semibold text-gray-900">Premier véhicule (optionnel)</h2>
              </div>
              <Switch checked={addVehicule} onCheckedChange={setAddVehicule} data-testid="toggle-add-vehicule" />
            </div>
            {addVehicule && (
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Plaque *</Label>
                  <Input value={vehicule.plaque} onChange={(e) => handleVehChange("plaque", e.target.value)} placeholder="GE 123 456" data-testid="veh-plaque" />
                </div>
                <div>
                  <Label>Marque *</Label>
                  <Input value={vehicule.marque} onChange={(e) => handleVehChange("marque", e.target.value)} data-testid="veh-marque" />
                </div>
                <div>
                  <Label>Modèle *</Label>
                  <Input value={vehicule.modele} onChange={(e) => handleVehChange("modele", e.target.value)} data-testid="veh-modele" />
                </div>
                <div>
                  <Label>Première mise en circulation</Label>
                  <Input type="date" value={vehicule.premiere_mec} onChange={(e) => handleVehChange("premiere_mec", e.target.value)} data-testid="veh-premiere-mec" />
                </div>
                <div>
                  <Label>Carburant</Label>
                  <Select value={vehicule.carburant} onValueChange={(v) => handleVehChange("carburant", v)}>
                    <SelectTrigger data-testid="veh-carburant"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="essence">Essence</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="hybride">Hybride</SelectItem>
                      <SelectItem value="electrique">Électrique</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kilométrage</Label>
                  <Input type="number" value={vehicule.km_dernier} onChange={(e) => handleVehChange("km_dernier", e.target.value)} data-testid="veh-km" />
                </div>
                <div>
                  <Label>VIN</Label>
                  <Input value={vehicule.vin} onChange={(e) => handleVehChange("vin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17))} maxLength={17} data-testid="veh-vin" />
                </div>
              </div>
            )}
          </Card>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="cancel-button">Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
      </Tabs>
    </div>
  );
}
